import { Curve3 } from "@babylonjs/core/Maths/math.path";
import { Matrix, Quaternion, Vector2, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Axis, Space } from "@babylonjs/core/Maths/math.axis";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import type { Scene } from "@babylonjs/core/scene";
import type { PadKind, TrackSpec } from "../config/tracks";

/** Result of locating a world position relative to the track loop (plan view:
 * scenery and the editors use this; the craft use {@link Track.project}). */
export interface TrackSample {
  /** 0..1 progress around the loop. */
  t: number;
  /** Signed lateral distance from centre line (+ = right of travel). */
  lateral: number;
  /** Road surface height at this point. */
  height: number;
  /** Unit forward (direction of travel) on the XZ plane. */
  forward: Vector3;
  /** Centre-line point at this location. */
  center: Vector3;
  /** Local road half-width here (sections can widen/narrow). */
  halfWidth: number;
  /** Surface bank (roll) in radians; positive raises the right edge. */
  bank: number;
}

/**
 * Where a world position sits on the road surface, in the road's own frame:
 * how far along (t), across (u, + = right) and above the surface (h) it is,
 * and the road's forward / up / right there. The road can face any way — up a
 * wall, round a loop, upside down — and this is what the craft ride on.
 */
export interface RoadHit {
  /** Sample index of the segment the position is on, and how far along it. */
  i: number;
  f: number;
  /** 0..1 progress round the loop. */
  t: number;
  /** Centre-line point level with the position. */
  center: Vector3;
  /** Across the road (+ = right of travel) and above its surface. */
  u: number;
  h: number;
  /** The road's frame here: along travel, surface normal, and across. */
  forward: Vector3;
  up: Vector3;
  right: Vector3;
  halfWidth: number;
  /** Distance from the position to the centre line (how sure the match is). */
  dist: number;
  /** The road's up and right at its centre line: the same as up/right on a
   * flat road, but where the road curls (a pipe, a cylinder) up/right are the
   * surface's under the position and these stay the centre line's. */
  spineUp: Vector3;
  spineRight: Vector3;
  /** How the cross-section curls: 1/radius, + up into a pipe, - down round a
   * cylinder, 0 = flat. */
  kappa: number;
  /** The cross-section wraps all the way round (a closed pipe or cylinder):
   * there are no edges, and u runs on round. */
  closed: boolean;
}

/** Where lateral position `u` sits on a cross-section curling at `kappa`
 * (1/radius; + curls up into a pipe, - down round a cylinder, 0 = flat): its
 * offset from the centre line, given the road's up and right there. */
export function crossOffset(up: Vector3, right: Vector3, kappa: number, u: number): Vector3 {
  if (kappa === 0) return right.scale(u);
  const a = kappa * u;
  const s = Math.sin(a / 2);
  return up.scale((2 * s * s) / kappa).addInPlace(right.scale(Math.sin(a) / kappa));
}

/** The surface normal at lateral `u` on that cross-section. */
export function crossNormal(up: Vector3, right: Vector3, kappa: number, u: number): Vector3 {
  if (kappa === 0) return up.clone();
  const a = kappa * u;
  return up.scale(Math.cos(a)).subtractInPlace(right.scale(Math.sin(a)));
}

/** The direction across the surface (+ = on round) at lateral `u`. */
export function crossRight(up: Vector3, right: Vector3, kappa: number, u: number): Vector3 {
  if (kappa === 0) return right.clone();
  const a = kappa * u;
  return right.scale(Math.cos(a)).addInPlace(up.scale(Math.sin(a)));
}

/** A cross-section this close to a full turn counts as closed. */
const CLOSED_WRAP = Math.PI * 2 - 1e-6;

/** Road colours: the grid cell and line (CSS colours), the self-lit glow over
 * them, and the edge rails. A course's theme sets these. */
export interface RoadPalette {
  cell: string;
  line: string;
  glow: Color3;
  rail: Color3;
}

/** The original neon look (Neon Circuit and editor tracks). */
export const NEON_ROAD: RoadPalette = {
  cell: "#0a0e1c",
  line: "#3aa6e6",
  glow: new Color3(0.55, 0.6, 0.7),
  rail: new Color3(0.1, 0.6, 1.0),
};

export interface Pad {
  kind: PadKind;
  position: Vector3;
  /** Along the pad (its launch/boost direction), in the road surface. */
  forward: Vector3;
  /** The road's surface normal at the pad. */
  up: Vector3;
  mesh: Mesh;
  /** Launch strength for jump pads (1 = small hop). */
  power: number;
  /** Trigger footprint: half the pad's width across and length along its
   * forward, matching the mesh, so it fires wherever you visibly drive over it. */
  halfWidth: number;
  halfLength: number;
}

const SAMPLES_PER_SEGMENT = 24;
/** Samples either side of the nearest one that still count as the same pass of
 * the loop (so a crossing is only another pass further round it). */
const SAME_PASS_WINDOW = SAMPLES_PER_SEGMENT * 2;
/** How much closer in height (world units) another pass must be before a
 * stacked section is picked over the nearest one in plan view. */
const STACK_MARGIN = 4;
/** project(): samples either side of the hint searched each frame. A craft
 * moves well under one sample per frame, so this only has to cover drift. */
const PROJECT_WINDOW = SAMPLES_PER_SEGMENT * 3;
/** project(): past this far outside the road the hinted search is treated as
 * lost and the whole loop is searched. */
const PROJECT_LOST = 60;
/** Strips across the road where it curls (a closed pipe's full turn). */
const ROAD_STRIPS = 24;
/** Glowing hoops round a pipe or cylinder, this far apart along it. */
const HOOP_SPACING = 70;
const HOOP_SEGMENTS = 64;

/** Spherical interpolation between two unit vectors. */
function slerpUnit(a: Vector3, b: Vector3, s: number): Vector3 {
  const d = Math.max(-1, Math.min(1, Vector3.Dot(a, b)));
  const th = Math.acos(d);
  if (th < 1e-4) return Vector3.Lerp(a, b, s).normalize();
  const sn = Math.sin(th);
  return a.scale(Math.sin((1 - s) * th) / sn).addInPlace(b.scale(Math.sin(s * th) / sn));
}

/** Point a flat mesh (built facing +Y, long along +Z) along `forward` with
 * `up` as its face. */
function orient(mesh: Mesh, forward: Vector3, up: Vector3): void {
  const right = Vector3.Cross(up, forward).normalize();
  const f = Vector3.Cross(right, up).normalize();
  const m = new Matrix();
  Matrix.FromXYZAxesToRef(right, up, f, m);
  mesh.rotationQuaternion = Quaternion.FromRotationMatrix(m);
}

/** The road sampled along its centre line: position and 3D frame (forward,
 * surface normal, across) per sample, with the plan-view direction, bank and
 * half-width. Used to build the track, and by the course-select previews. */
export interface RoadFrames {
  centers: Vector3[];
  forward: Vector3[];
  up: Vector3[];
  across: Vector3[];
  planForward: Vector3[];
  planRight: Vector3[];
  bank: number[];
  halfWidth: number[];
  /** How far round the cross-section wraps (radians): 0 flat, π a half-pipe,
   * 2π a closed pipe; negative curls down round a cylinder. */
  wrap: number[];
}

/** The cross-section's curvature for a wrap and half-width. */
export function curlOf(wrap: number, halfWidth: number): number {
  return wrap === 0 ? 0 : wrap / (2 * halfWidth);
}

export function roadFrames(spec: TrackSpec, samplesPerSegment: number): RoadFrames {
  const ctrl = spec.points.map((p) => new Vector3(p[0], p[1], p[2]));
  const pts = Curve3.CreateCatmullRomSpline(ctrl, samplesPerSegment, true).getPoints();
  // CreateCatmullRomSpline(closed) repeats the first point at the end; drop it.
  const centers = pts.slice(0, pts.length - 1);
  const n = centers.length;
  const nseg = spec.points.length;
  const forward: Vector3[] = [];
  const planForward: Vector3[] = [];
  const planRight: Vector3[] = [];

  // Tangents: along the curve in 3D, and its plan-view (XZ) direction. Where the
  // road runs straight up or down (a loop) the plan direction is undefined, so
  // it carries the last good one.
  let lastPlan = new Vector3(0, 0, 1);
  for (let i = 0; i < n; i++) {
    const t3 = centers[(i + 1) % n].subtract(centers[(i - 1 + n) % n]);
    const len = t3.length();
    forward.push(len > 1e-9 ? t3.scaleInPlace(1 / len) : (forward[i - 1] ?? new Vector3(0, 0, 1)).clone());
    const plan = new Vector3(t3.x, 0, t3.z);
    const pl = plan.length();
    if (pl > 1e-4) lastPlan = plan.scaleInPlace(1 / pl);
    planForward.push(lastPlan.clone());
    // right = tangent x up
    planRight.push(new Vector3(lastPlan.z, 0, -lastPlan.x));
  }

  // Per-sample bank, width and up. The closed Catmull-Rom's segment k runs from
  // control point k+1 to k+2, so each sample takes its attributes from those two
  // points, eased with a smoothstep so nothing snaps at a control point.
  const segBank = (idx: number) => ((spec.points[idx % nseg][3] ?? 0) * Math.PI) / 180;
  const segWidth = (idx: number) => spec.points[idx % nseg][4] ?? spec.roadHalfWidth;
  const segWrap = (idx: number) => (Math.max(-360, Math.min(360, spec.points[idx % nseg][5] ?? 0)) * Math.PI) / 180;
  const segUp = (idx: number): Vector3 | null => {
    const u = spec.ups?.[idx % nseg];
    return u ? new Vector3(u[0], u[1], u[2]).normalize() : null;
  };
  const bank: number[] = [];
  const halfWidth: number[] = [];
  const wrap: number[] = [];
  const up: Vector3[] = [];
  const across: Vector3[] = [];
  for (let i = 0; i < n; i++) {
    const f = i / samplesPerSegment;
    const k = Math.floor(f);
    const frac = f - k;
    const s = frac * frac * (3 - 2 * frac);
    const a = k + 1;
    const b = k + 2;
    bank.push(segBank(a) + (segBank(b) - segBank(a)) * s);
    halfWidth.push(segWidth(a) + (segWidth(b) - segWidth(a)) * s);
    const wa = segWrap(a);
    const wb = segWrap(b);
    wrap.push(wa === wb ? wa : wa + (wb - wa) * s);

    // The usual frame: level across, tilted by the bank (the plan right rolled
    // about the direction of travel; + raises the right edge). A control point
    // with its own up overrides it, easing between the two.
    const t3 = forward[i];
    const r = planRight[i];
    const cb = Math.cos(bank[i]);
    const legacyUp = Vector3.Cross(t3, new Vector3(r.x * cb, Math.sin(bank[i]), r.z * cb)).normalize();
    const ea = segUp(a);
    const eb = segUp(b);
    const ua = ea ?? legacyUp;
    const ub = eb ?? legacyUp;
    // Between two given ups the road turns at an even rate (a corkscrew rolls
    // smoothly); easing in and out of a given up from the usual frame is
    // smoothstepped like the bank.
    const u = ua === ub ? ua.clone() : slerpUnit(ua, ub, ea && eb ? frac : s);
    // Square the frame up with the direction of travel.
    u.subtractInPlace(t3.scale(Vector3.Dot(u, t3)));
    if (u.lengthSquared() < 1e-9) u.copyFrom(up[i - 1] ?? Vector3.Up());
    u.normalize();
    up.push(u);
    across.push(Vector3.Cross(u, t3).normalize());
  }
  return { centers, forward, up, across, planForward, planRight, bank, halfWidth, wrap };
}

export class Track {
  readonly spec: TrackSpec;
  readonly halfWidth: number;

  /** Dense centre-line samples (closed loop; last != first). */
  private centers: Vector3[] = [];
  /** Plan-view (XZ) tangent per sample — scenery, editors, locate(). */
  private tangents: Vector3[] = [];
  /** The road's 3D frame per sample: along travel, surface normal, across. */
  private fwd3: Vector3[] = [];
  private ups: Vector3[] = [];
  private across: Vector3[] = [];
  /** Per-sample bank (roll) in radians, interpolated from the control points. */
  private banks: number[] = [];
  /** Per-sample road half-width, interpolated from the control points. */
  private halfWidths: number[] = [];
  /** Per-sample cross-section wrap (radians; 0 = flat), and whether any of the
   * road curls at all (a flat course keeps the plain ribbon and rails). */
  private wraps: number[] = [];
  private curled = false;
  private cumLen: number[] = [];
  private totalLen = 0;
  /** Scratch for locate(): squared plan-view distance and projection per segment. */
  private segDist = new Float64Array(0);
  private segProj = new Float64Array(0);

  readonly pads: Pad[] = [];
  readonly startPosition = new Vector3();
  readonly startForward = new Vector3(0, 0, 1);
  readonly startUp = new Vector3(0, 1, 0);

  road!: Mesh;
  private roadTex!: DynamicTexture;
  private roadMat!: StandardMaterial;
  private railMat!: StandardMaterial;
  /** Every mesh this track owns, so it can be torn down on a rebuild. */
  private meshes: Mesh[] = [];

  constructor(scene: Scene, spec: TrackSpec) {
    this.spec = spec;
    this.halfWidth = spec.roadHalfWidth;
    this.buildCenterline(spec);
    this.buildRoadMesh(scene);
    this.buildPads(scene, spec);

    this.startPosition.copyFrom(this.centers[0]);
    this.startForward.copyFrom(this.tangents[0]);
    this.startUp.copyFrom(this.ups[0]);
  }

  // ---- geometry construction ------------------------------------------------

  private buildCenterline(spec: TrackSpec): void {
    const f = roadFrames(spec, SAMPLES_PER_SEGMENT);
    this.centers = f.centers;
    this.fwd3 = f.forward;
    this.ups = f.up;
    this.across = f.across;
    this.tangents = f.planForward;
    this.banks = f.bank;
    this.halfWidths = f.halfWidth;
    this.wraps = f.wrap;
    this.curled = f.wrap.some((w) => w !== 0);
    const n = this.centers.length;
    this.cumLen = [0];
    for (let i = 1; i < n; i++) {
      this.totalLen += Vector3.Distance(this.centers[i], this.centers[i - 1]);
      this.cumLen.push(this.totalLen);
    }
    // close the loop length
    this.totalLen += Vector3.Distance(this.centers[n - 1], this.centers[0]);
  }

  private buildRoadMesh(scene: Scene): void {
    const n = this.centers.length;
    if (!this.curled) {
      const left: Vector3[] = [];
      const right: Vector3[] = [];
      for (let i = 0; i <= n; i++) {
        const j = i % n;
        const c = this.centers[j];
        const a = this.across[j];
        const up = this.ups[j].scale(0.02);
        const hw = this.halfWidths[j] ?? this.halfWidth;
        left.push(c.add(a.scale(-hw)).addInPlace(up));
        right.push(c.add(a.scale(hw)).addInPlace(up));
      }
      this.road = MeshBuilder.CreateRibbon(
        "road",
        { pathArray: [left, right], closeArray: false, closePath: false },
        scene
      );
    } else {
      this.road = this.buildCurledRoad(scene);
    }
    const tex = this.makeGridTexture(scene);
    // Tile the grid by world size so cells stay ~square and lots of lines rush
    // past at speed (the frame-of-reference the flat surface was missing).
    const CELL = 7;
    // On this ribbon, U runs across the width and V along the length.
    tex.uScale = (this.halfWidth * 2) / CELL;
    tex.vScale = this.totalLen / CELL;

    const mat = new StandardMaterial("roadMat", scene);
    mat.diffuseTexture = tex;
    mat.emissiveTexture = tex; // make the grid lines self-lit so they read in fog
    mat.emissiveColor = NEON_ROAD.glow.clone();
    mat.specularColor = new Color3(0.04, 0.04, 0.06);
    // Seen from underneath too: looking up at a loop, or at the deck above a
    // crossing.
    mat.backFaceCulling = false;
    mat.twoSidedLighting = true;
    this.road.material = mat;
    this.roadTex = tex;
    this.roadMat = mat;
    this.meshes.push(this.road);

    this.buildEdgeRails(scene);
    this.buildStartLine(scene);
  }

  /**
   * The road where it curls somewhere. The flat stretches are the plain
   * two-edge ribbon; only where it curls into a pipe or round a cylinder is it
   * cut into strips across, to bend on. Every piece carries the texture
   * coordinates the single ribbon would give it (along: the distance down
   * each edge over that edge's length; across: 0 to 1), so the grid runs on
   * unbroken from piece to piece, and it's one mesh again at the end.
   */
  private buildCurledRoad(scene: Scene): Mesh {
    const n = this.centers.length;
    // Row i (0..n, n being 0 again) of the road, `k` strips of `strips` across.
    const at = (i: number, k: number, strips: number) => {
      const j = i % n;
      const hw = this.halfWidths[j] ?? this.halfWidth;
      const kappa = curlOf(this.wraps[j], hw);
      const u = -hw + (2 * hw * k) / strips;
      const p = this.centers[j].add(crossOffset(this.ups[j], this.across[j], kappa, u));
      return p.addInPlace(crossNormal(this.ups[j], this.across[j], kappa, u).scaleInPlace(0.02));
    };
    const along = (k: number) => {
      const d = [0];
      let prev = at(0, k, 1);
      for (let i = 1; i <= n; i++) {
        const p = at(i, k, 1);
        d.push(d[i - 1] + Vector3.Distance(p, prev));
        prev = p;
      }
      return d.map((x) => x / (d[n] || 1));
    };
    const uL = along(0);
    const uR = along(1);
    // Runs of rows that curl (a segment curls if either end does) and that
    // don't; neighbouring runs share the row between them.
    const curls = (r: number) => this.wraps[r % n] !== 0 || this.wraps[(r + 1) % n] !== 0;
    const pieces: Mesh[] = [];
    let start = 0;
    for (let r = 0; r < n; r++) {
      if (r + 1 < n && curls(r + 1) === curls(start)) continue;
      const end = r + 1;
      const strips = curls(start) ? ROAD_STRIPS : 1;
      const pathArray: Vector3[][] = [];
      const uvs: Vector2[] = [];
      for (let k = 0; k <= strips; k++) {
        const path: Vector3[] = [];
        for (let i = start; i <= end; i++) {
          path.push(at(i, k, strips));
          uvs.push(new Vector2(uL[i] + ((uR[i] - uL[i]) * k) / strips, k / strips));
        }
        pathArray.push(path);
      }
      pieces.push(MeshBuilder.CreateRibbon("roadPiece", { pathArray, uvs, closeArray: false, closePath: false }, scene));
      start = end;
    }
    const road = pieces.length > 1 ? Mesh.MergeMeshes(pieces, true, true) : pieces[0];
    if (!road) throw new Error("couldn't build the road");
    road.name = "road";
    return road;
  }

  /** The most basic crosshatch: one cell with bright lines on two edges that
   * tiles into a continuous square grid across the track. */
  private makeGridTexture(scene: Scene): DynamicTexture {
    const s = 64;
    // mipmaps + anisotropy keep it clean at grazing angles down the track.
    const tex = new DynamicTexture("roadGrid", { width: s, height: s }, scene, true);
    tex.anisotropicFilteringLevel = 8;
    this.paintGrid(tex, NEON_ROAD);
    tex.wrapU = 1; // WRAP
    tex.wrapV = 1;
    return tex;
  }

  private paintGrid(tex: DynamicTexture, p: RoadPalette): void {
    const s = tex.getSize().width;
    const ctx = tex.getContext() as CanvasRenderingContext2D;
    ctx.fillStyle = p.cell; // dark cell
    ctx.fillRect(0, 0, s, s);
    ctx.fillStyle = p.line; // single-weight grid lines
    ctx.fillRect(0, 0, s, 3); // top edge
    ctx.fillRect(0, 0, 3, s); // left edge
    tex.update();
  }

  /** Recolour the road grid, its glow and the edge rails (a course's theme). */
  setPalette(p: RoadPalette): void {
    this.paintGrid(this.roadTex, p);
    this.roadMat.emissiveColor = p.glow.clone();
    this.railMat.emissiveColor = p.rail.clone();
    this.railMat.diffuseColor = p.rail.scale(0.2);
  }

  private buildEdgeRails(scene: Scene): void {
    const n = this.centers.length;
    const mat = new StandardMaterial("railMat", scene);
    this.railMat = mat;
    mat.emissiveColor = NEON_ROAD.rail.clone();
    mat.diffuseColor = new Color3(0.05, 0.1, 0.2);

    for (const side of [-1, 1]) {
      const path: Vector3[] = [];
      for (let i = 0; i <= n; i++) {
        const j = i % n;
        const hw = this.halfWidths[j] ?? this.halfWidth;
        const w = this.wraps[j];
        if (w === 0) {
          path.push(this.centers[j].add(this.across[j].scale(side * (hw + 0.5))).addInPlace(this.ups[j].scale(1.2)));
          continue;
        }
        // Where the road curls shut its two edges meet: the rails close in to
        // a seam lying flat along the join, which a craft drives straight over.
        const open = Math.max(0, Math.min(1, (Math.PI * 2 - Math.abs(w)) / (Math.PI / 4)));
        const kappa = curlOf(w, hw);
        const p = this.centers[j].add(crossOffset(this.ups[j], this.across[j], kappa, side * (hw + 0.5 * open)));
        path.push(p.addInPlace(crossNormal(this.ups[j], this.across[j], kappa, side * hw).scaleInPlace(1.2 * open)));
      }
      const rail = MeshBuilder.CreateTube(
        `rail${side}`,
        { path, radius: 0.35, tessellation: 6, cap: Mesh.NO_CAP },
        scene
      );
      rail.material = mat;
      this.meshes.push(rail);
    }
    if (this.curled) this.buildHoops(scene, mat);
  }

  /** Glowing hoops round the inside of a pipe (and the outside of a
   * cylinder), every HOOP_SPACING along it: they rush past overhead and round
   * the sides, so speed reads when the grid is all around you. */
  private buildHoops(scene: Scene, mat: StandardMaterial): void {
    const n = this.centers.length;
    const hoops: Mesh[] = [];
    let next = 0;
    for (let i = 0; i < n; i++) {
      const w = this.wraps[i];
      if (Math.abs(w) < Math.PI) {
        next = this.cumLen[i] + HOOP_SPACING / 2;
        continue;
      }
      if (this.cumLen[i] < next) continue;
      next = this.cumLen[i] + HOOP_SPACING;
      const hw = this.halfWidths[i] ?? this.halfWidth;
      const kappa = curlOf(w, hw);
      const path: Vector3[] = [];
      for (let k = 0; k <= HOOP_SEGMENTS; k++) {
        const u = -hw + (2 * hw * k) / HOOP_SEGMENTS;
        const p = this.centers[i].add(crossOffset(this.ups[i], this.across[i], kappa, u));
        path.push(p.addInPlace(crossNormal(this.ups[i], this.across[i], kappa, u).scaleInPlace(0.1)));
      }
      hoops.push(MeshBuilder.CreateTube("hoop", { path, radius: 0.14, tessellation: 4, cap: Mesh.NO_CAP }, scene));
    }
    if (!hoops.length) return;
    const merged = Mesh.MergeMeshes(hoops, true, true);
    if (!merged) return;
    merged.name = "hoops";
    merged.material = mat;
    this.meshes.push(merged);
  }

  private buildStartLine(scene: Scene): void {
    const plane = MeshBuilder.CreateGround(
      "startLine",
      { width: (this.halfWidths[0] ?? this.halfWidth) * 2, height: 4 },
      scene
    );
    plane.position = this.centers[0].add(this.ups[0].scale(0.05));
    orient(plane, this.fwd3[0], this.ups[0]);
    const mat = new StandardMaterial("startMat", scene);
    mat.emissiveColor = new Color3(1, 1, 1);
    mat.diffuseColor = new Color3(0.8, 0.8, 0.8);
    mat.backFaceCulling = false;
    plane.material = mat;
    this.meshes.push(plane);
  }

  private buildPads(scene: Scene, spec: TrackSpec): void {
    for (const p of spec.pads) {
      const idx = Math.floor(p.t * this.centers.length) % this.centers.length;
      const c = this.centers[idx];
      const hw = this.halfWidths[idx] ?? this.halfWidth;
      const lat = p.offset * (hw - 3);
      // The spot on the road at the pad's offset, and the surface's frame there
      // (on a pipe or a cylinder that's round the curve, not straight out).
      let up = this.ups[idx];
      let across = this.across[idx];
      let spot = c.add(across.scale(lat));
      const kappa = curlOf(this.wraps[idx], hw);
      if (kappa !== 0) {
        spot = c.add(crossOffset(up, across, kappa, lat));
        const n = crossNormal(up, across, kappa, lat);
        across = crossRight(up, across, kappa, lat);
        up = n;
      }
      // Aim the pad off the track direction by its angle (degrees, + = right).
      // The angle is a plan-view heading (that's what a jump line is drawn
      // with), so turn the plan tangent, then tip it into the road surface
      // without changing that heading. Only where the road is too steep for a
      // plan heading (a wall, a loop) does it turn within the surface instead.
      const a = ((p.angle ?? 0) * Math.PI) / 180;
      const ca = Math.cos(a);
      const sa = Math.sin(a);
      const t = this.tangents[idx];
      const f =
        up.y > 0.3
          ? new Vector3(t.x * ca + t.z * sa, 0, -t.x * sa + t.z * ca)
          : this.fwd3[idx].scale(ca).addInPlace(across.scale(sa));
      if (up.y > 0.3) f.y = -(f.x * up.x + f.z * up.z) / up.y;
      f.normalize();
      const power = p.power ?? 1;
      const pos = spot.addInPlace(up.scale(0.1));

      const mat = new StandardMaterial(`padMat-${idx}`, scene);
      mat.emissiveColor =
        p.kind === "boost" ? new Color3(1, 0.8, 0.1) : new Color3(0.3, 1, 0.5);
      mat.diffuseColor = mat.emissiveColor.scale(0.4);
      mat.backFaceCulling = false;

      // A jump pad reads as a ramp: a plate tilted up toward travel, sized by
      // its launch power so a big shortcut ramp looks the part.
      const width = p.kind === "boost" ? 16 : 18;
      const len = p.kind === "boost" ? 18 : 12 + power * 8;
      const mesh = MeshBuilder.CreateGround(`pad-${p.kind}-${idx}`, { width, height: len }, scene);
      mesh.position = pos.clone();
      orient(mesh, f, up);
      if (p.kind === "jump") {
        // Pitch the leading edge upward about the local cross-track axis.
        mesh.rotate(Axis.X, -0.32 * power, Space.LOCAL);
        mesh.position.addInPlace(up.scale(0.6 * power));
      }
      mesh.material = mat;

      this.meshes.push(mesh);
      this.pads.push({ kind: p.kind, position: pos, forward: f, up: up.clone(), mesh, power, halfWidth: width / 2, halfLength: len / 2 });
    }
  }

  /** Tear down all geometry this track created (for a rebuild/track-swap). */
  dispose(): void {
    for (const m of this.meshes) m.dispose(false, true);
    this.meshes = [];
    this.pads.length = 0;
  }

  // ---- queries --------------------------------------------------------------

  /** Number of centre-line samples (hint indices for project() run 0..n-1). */
  get sampleCount(): number {
    return this.centers.length;
  }

  /**
   * Where a world position sits on the road, in the road's frame. `hint` is the
   * sample index from the last call for the same craft: the search stays near
   * it, so a craft keeps to its own pass of the loop at a crossing or inside a
   * loop, and it's cheap. Without a hint (or if the craft is well off the road
   * near it) the whole loop is searched.
   */
  project(pos: Vector3, hint = -1): RoadHit {
    const n = this.centers.length;
    let best = -1;
    let bestD = Infinity;
    let bestF = 0;
    const test = (i: number) => {
      const a = this.centers[i];
      const b = this.centers[(i + 1) % n];
      const abx = b.x - a.x;
      const aby = b.y - a.y;
      const abz = b.z - a.z;
      const len2 = abx * abx + aby * aby + abz * abz || 1e-9;
      let f = ((pos.x - a.x) * abx + (pos.y - a.y) * aby + (pos.z - a.z) * abz) / len2;
      f = f < 0 ? 0 : f > 1 ? 1 : f;
      const dx = pos.x - (a.x + abx * f);
      const dy = pos.y - (a.y + aby * f);
      const dz = pos.z - (a.z + abz * f);
      const d = dx * dx + dy * dy + dz * dz;
      if (d < bestD) {
        bestD = d;
        best = i;
        bestF = f;
      }
    };
    if (hint >= 0 && hint < n) {
      for (let k = -PROJECT_WINDOW; k <= PROJECT_WINDOW; k++) test((hint + k + n) % n);
    }
    if (best < 0 || Math.sqrt(bestD) > (this.halfWidths[best] ?? this.halfWidth) + PROJECT_LOST) {
      best = -1;
      bestD = Infinity;
      for (let i = 0; i < n; i++) test(i);
    }
    return this.hitAt(pos, best, bestF, Math.sqrt(bestD));
  }

  /** The road's frame at its centre line, segment i, fraction f: where it is,
   * which way it runs, its up and right, width, and how its cross-section
   * curls (wrap, radians; kappa = 1/radius). */
  private frame(i: number, f: number) {
    const n = this.centers.length;
    const j = (i + 1) % n;
    const center = Vector3.Lerp(this.centers[i], this.centers[j], f);
    const forward = Vector3.Lerp(this.fwd3[i], this.fwd3[j], f).normalize();
    const up = Vector3.Lerp(this.ups[i], this.ups[j], f);
    up.subtractInPlace(forward.scale(Vector3.Dot(up, forward))).normalize();
    const right = Vector3.Cross(up, forward).normalize();
    const hwA = this.halfWidths[i] ?? this.halfWidth;
    const hwB = this.halfWidths[j] ?? this.halfWidth;
    const halfWidth = hwA + (hwB - hwA) * f;
    const wA = this.wraps[i];
    const wB = this.wraps[j];
    const wrap = wA === wB ? wA : wA + (wB - wA) * f;
    return { center, forward, up, right, halfWidth, wrap, kappa: curlOf(wrap, halfWidth) };
  }

  /** The road frame at segment i, fraction f, and `pos` expressed in it. */
  private hitAt(pos: Vector3, i: number, f: number, dist: number): RoadHit {
    const n = this.centers.length;
    const { center, forward, up, right, halfWidth, wrap, kappa } = this.frame(i, f);
    const d = pos.subtract(center);
    if (kappa === 0) {
      return {
        i,
        f,
        t: (i + f) / n,
        center,
        u: Vector3.Dot(d, right),
        h: Vector3.Dot(d, up),
        forward,
        up,
        right,
        halfWidth,
        dist,
        spineUp: up,
        spineRight: right,
        kappa: 0,
        closed: false,
      };
    }
    // A curled cross-section is an arc of radius 1/kappa about an axis along
    // the road (above the centre line for a pipe, below it for a cylinder).
    // How far round that arc the position is gives u (as a distance along the
    // surface), how far off it gives h, and the surface's frame turns with it.
    const x = Vector3.Dot(d, right);
    const y = Vector3.Dot(d, up);
    const rho = 1 / kappa;
    const s = kappa > 0 ? 1 : -1;
    const phi = Math.atan2(s * x, s * (rho - y));
    const c = Math.cos(phi);
    const sn = Math.sin(phi);
    return {
      i,
      f,
      t: (i + f) / n,
      center,
      u: phi * rho,
      h: s * (Math.abs(rho) - Math.hypot(x, y - rho)),
      forward,
      up: up.scale(c).subtractInPlace(right.scale(sn)),
      right: right.scale(c).addInPlace(up.scale(sn)),
      halfWidth,
      dist,
      spineUp: up,
      spineRight: right,
      kappa,
      closed: Math.abs(wrap) >= CLOSED_WRAP,
    };
  }

  /** The road frame at progress t (0..1, wraps): at the centre line, or `u`
   * across it (which only differs where the road curls). */
  frameAt(t: number, u = 0): { center: Vector3; forward: Vector3; up: Vector3; right: Vector3; halfWidth: number } {
    const n = this.centers.length;
    const tt = t - Math.floor(t);
    const fi = tt * n;
    const i = Math.floor(fi) % n;
    const fr = this.frame(i, fi - Math.floor(fi));
    if (u === 0 || fr.kappa === 0) return fr;
    return {
      center: fr.center.add(crossOffset(fr.up, fr.right, fr.kappa, u)),
      forward: fr.forward,
      up: crossNormal(fr.up, fr.right, fr.kappa, u),
      right: crossRight(fr.up, fr.right, fr.kappa, u),
      halfWidth: fr.halfWidth,
    };
  }

  /** How far below its centre line (in world height) the road reaches at
   * progress t because it curls: nothing on flat road, the whole tube under a
   * cylinder. Scenery keeps beneath it. */
  reachBelow(t: number): number {
    const n = this.centers.length;
    const tt = t - Math.floor(t);
    const fi = tt * n;
    const fr = this.frame(Math.floor(fi) % n, fi - Math.floor(fi));
    if (fr.kappa === 0) return 0;
    let low = 0;
    for (let k = 0; k <= 16; k++) {
      const u = -fr.halfWidth + (fr.halfWidth * k) / 8;
      low = Math.min(low, crossOffset(fr.up, fr.right, fr.kappa, u).y);
    }
    return -low;
  }

  /**
   * Find where a world position sits relative to the track, in plan view (the
   * scenery and the editors use this; the craft ride {@link project}).
   * Brute-force nearest segment — cheap and exact for a few thousand samples.
   */
  locate(pos: Vector3): TrackSample {
    const n = this.centers.length;
    if (this.segDist.length !== n) {
      this.segDist = new Float64Array(n);
      this.segProj = new Float64Array(n);
    }
    let bestDist = Infinity;
    let bestI = 0;
    let bestProj = 0;

    for (let i = 0; i < n; i++) {
      const a = this.centers[i];
      const b = this.centers[(i + 1) % n];
      const ab = b.subtract(a);
      const abLen2 = ab.lengthSquared() || 1e-6;
      let tt = Vector3.Dot(pos.subtract(a), ab) / abLen2;
      tt = tt < 0 ? 0 : tt > 1 ? 1 : tt;
      const proj = a.add(ab.scale(tt));
      const d = (pos.x - proj.x) ** 2 + (pos.z - proj.z) ** 2;
      this.segDist[i] = d;
      this.segProj[i] = tt;
      if (d < bestDist) {
        bestDist = d;
        bestI = i;
        bestProj = tt;
      }
    }

    const alt = this.stackedPass(pos, bestI);
    if (alt >= 0) {
      bestI = alt;
      bestProj = this.segProj[alt];
    }

    const a = this.centers[bestI];
    const b = this.centers[(bestI + 1) % n];
    const center = Vector3.Lerp(a, b, bestProj);
    const forward = Vector3.Lerp(this.tangents[bestI], this.tangents[(bestI + 1) % n], bestProj);
    forward.y = 0;
    forward.normalize();
    const right = new Vector3(forward.z, 0, -forward.x);
    const lateral = Vector3.Dot(pos.subtract(center), right);

    // Banked sections tilt the surface: its edge sits hw*sin(bank) UP at
    // hw*cos(bank) ACROSS, so the height at a horizontal offset climbs by
    // lateral*tan(bank) and the road's visible half-width is hw*cos(bank).
    const bankA = this.banks[bestI] ?? 0;
    const bankB = this.banks[(bestI + 1) % n] ?? 0;
    const bank = bankA + (bankB - bankA) * bestProj;
    const hwA = this.halfWidths[bestI] ?? this.halfWidth;
    const hwB = this.halfWidths[(bestI + 1) % n] ?? this.halfWidth;
    const hwGeom = hwA + (hwB - hwA) * bestProj;
    // Past the road's edge the surface doesn't keep tilting: hold the edge height.
    const edge = hwGeom * Math.cos(bank);
    const height = center.y + Math.max(-edge, Math.min(edge, lateral)) * Math.tan(bank);

    return {
      t: (bestI + bestProj) / n,
      lateral,
      height,
      forward,
      center,
      halfWidth: hwGeom * Math.cos(bank),
      bank,
    };
  }

  /**
   * Where the loop crosses over itself (a bridge above another section, the
   * turns of a helix), the nearest centre line in plan view can be another
   * level. Every other pass of the loop that also runs under/over `pos` is a
   * candidate (its nearest segment); if one's surface is clearly closer in
   * height than the nearest's, return it, otherwise -1. Uses the per-segment
   * distances locate() just filled in.
   */
  private stackedPass(pos: Vector3, bestI: number): number {
    const n = this.centers.length;
    const covers = (i: number) => Math.sqrt(this.segDist[i]) <= (this.halfWidths[i] ?? this.halfWidth) + 1;
    if (!covers(bestI)) return -1;
    const yAt = (i: number) => {
      const a = this.centers[i];
      const b = this.centers[(i + 1) % n];
      return a.y + (b.y - a.y) * this.segProj[i];
    };
    let pick = -1;
    let pickDy = Math.abs(pos.y - yAt(bestI)) - STACK_MARGIN;
    const consider = (i: number) => {
      const dy = Math.abs(pos.y - yAt(i));
      if (dy < pickDy) {
        pickDy = dy;
        pick = i;
      }
    };
    // Walk the loop; each run of covering segments away from the nearest one is
    // another pass over this spot — take its nearest segment as the candidate.
    let run = -1;
    for (let i = 0; i < n; i++) {
      let di = Math.abs(i - bestI);
      di = Math.min(di, n - di);
      if (di > SAME_PASS_WINDOW && covers(i)) {
        if (run < 0 || this.segDist[i] < this.segDist[run]) run = i;
      } else if (run >= 0) {
        consider(run);
        run = -1;
      }
    }
    if (run >= 0) consider(run);
    return pick;
  }

  get length(): number {
    return this.totalLen;
  }

  /** Centre-line point at progress t (0..1, wraps), with optional lateral offset
   * in world units across the road surface. Used by the bot AI to aim down the
   * track. */
  pointAt(t: number, lateralOffset = 0): Vector3 {
    const n = this.centers.length;
    const tt = t - Math.floor(t); // wrap into [0,1)
    const f = tt * n;
    const i = Math.floor(f) % n;
    const frac = f - Math.floor(f);
    if (lateralOffset !== 0 && (this.wraps[i] !== 0 || this.wraps[(i + 1) % n] !== 0)) {
      // Round the curve of a pipe or a cylinder.
      const fr = this.frame(i, frac);
      return fr.center.add(crossOffset(fr.up, fr.right, fr.kappa, lateralOffset));
    }
    const p = Vector3.Lerp(this.centers[i], this.centers[(i + 1) % n], frac);
    if (lateralOffset !== 0) p.addInPlace(Vector3.Lerp(this.across[i], this.across[(i + 1) % n], frac).scale(lateralOffset));
    return p;
  }

  /** Flat XZ centre-line loop, for drawing the minimap outline. */
  centerlineXZ(): { x: number; z: number }[] {
    return this.centers.map((c) => ({ x: c.x, z: c.z }));
  }
}
